/**
 * Goods distributed by program (migration 0088; the inventory plan's §15.7,
 * step I2b), a Reports tab for a business that keeps goods and has a project.
 * For each program, then item, the goods whose cost was charged to it in the
 * period, on each expense account.
 *
 * THE ACCOUNT IS A COLUMN, NOT A FILTER. Nothing but the expense the operator
 * chose tells goods given from goods spoiled, so shrinkage shows as its own
 * row rather than vanishing. What was SOLD is left out: a sale's cost, and any
 * cost on a cost-of-sales account.
 */

import { Fragment } from 'react';
import type { GoodsByProgram } from '../api.js';
import { formatCents } from '../lib/money.js';
import { showQuantity } from '../pages/ItemsPage.js';
import { InfoNote } from './InfoNote.js';

export function GoodsByProgramReport({ report, title, word }: { report: GoodsByProgram; title: string; word: string }) {
  return (
    <div className="report">
      <h2>{title}</h2>
      <p className="muted">
        {report.from} to {report.to}.{' '}
        <InfoNote label="What Counts Here">
          The cost of goods taken out of stock to an expense, by the {word.toLowerCase()} each cost was charged to. Goods
          sold are left out, and so is any cost on a cost-of-sales account. A movement reversed counts nothing. Each
          expense account is its own row, so goods written off to shrinkage show apart from goods given.
        </InfoNote>
      </p>
      {report.programs.length === 0 ? (
        <p className="muted">No goods were charged to an expense in this period.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines" aria-label={title}>
            <thead>
              <tr>
                <th>{word}</th>
                <th>Item</th>
                <th>Account</th>
                <th className="num">Quantity</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {report.programs.map((program) => (
                <Fragment key={program.projectId ?? 'none'}>
                  {program.rows.map((row, index) => (
                    <tr key={`${program.projectId ?? 'none'}-${row.itemId}-${row.accountId}`}>
                      <td>{index === 0 ? (program.projectName ?? `No ${word}`) : ''}</td>
                      <td>{row.item}</td>
                      <td>
                        {row.accountCode} {row.accountName}
                      </td>
                      <td className="num">
                        {showQuantity(row.quantity)} {row.unit}
                      </td>
                      <td className="num">{formatCents(row.cents)}</td>
                    </tr>
                  ))}
                  <tr className="subtotal">
                    <td colSpan={4}>Total, {program.projectName ?? `No ${word}`}</td>
                    <td className="num">{formatCents(program.totalCents)}</td>
                  </tr>
                </Fragment>
              ))}
              <tr className="total">
                <td colSpan={4}>Total</td>
                <td className="num">{formatCents(report.totalCents)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
